import en from './messages/en.json';
import fr from './messages/fr.json';
import ar from './messages/ar.json';
// The Guide is long-form prose and lives in its own files, merged in under the
// `guide` namespace. Keeping it out of the interface dictionaries is what makes
// both readable — one is a list of labels, the other is a manual.
import guideEn from './guide/en.json';
import guideFr from './guide/fr.json';
import guideAr from './guide/ar.json';

export const LOCALES = ['en', 'fr', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** Arabic is the only RTL locale today; keep the check data-driven anyway. */
const RTL_LOCALES = new Set<Locale>(['ar']);

export const messages = {
  en: { ...en, guide: guideEn },
  fr: { ...fr, guide: guideFr },
  ar: { ...ar, guide: guideAr },
} satisfies Record<Locale, unknown>;

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function dirFor(locale: Locale): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

/** Locale is the first path segment: /en/..., /fr/..., /ar/... */
export function localeFromPath(pathname: string): Locale | null {
  const first = pathname.split('/').filter(Boolean)[0];
  return first && isLocale(first) ? first : null;
}

export function resolveLocale(pathname: string, accept: string): Locale {
  const fromPath = localeFromPath(pathname);
  if (fromPath) return fromPath;
  for (const tag of accept.split(',')) {
    const base = tag.trim().split(/[-;]/)[0]?.toLowerCase();
    if (base && isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}
